import { bookingWorkspacePath } from "../../api/bookingWorkspaceApi";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleX,
  CreditCard,
  Eye,
  Loader2,
  Package,
  RefreshCcw,
  Search,
  ShoppingBag,
  UserRound,
  X,
} from "lucide-react";
import { useAuth } from "../../app/providers/AuthProvider";
import { ordersApi } from "../../api/ordersApi";
import { commercePaymentsApi } from "../../api/commercePaymentsApi";
import { FK } from "../../capabilities/featureKeys";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  "Received",
  "Accepted",
  "InProgress",
  "Ready",
  "Completed",
  "Rejected",
  "Cancelled",
];
const STATUS_LABEL = {
  Received: "Received",
  Accepted: "Accepted",
  InProgress: "In Progress",
  Ready: "Ready",
  Completed: "Completed",
  Rejected: "Rejected",
  Cancelled: "Cancelled",
};
const STATUS_TONE = {
  Received: "border-sky-200 bg-sky-50 text-sky-700",
  Accepted: "border-emerald-200 bg-emerald-50 text-emerald-700",
  InProgress: "border-amber-200 bg-amber-50 text-amber-800",
  Ready: "border-teal-200 bg-teal-50 text-teal-700",
  Completed: "border-slate-200 bg-slate-100 text-slate-700",
  Rejected: "border-rose-200 bg-rose-50 text-rose-700",
  Cancelled: "border-zinc-200 bg-zinc-100 text-zinc-600",
};
const STATUS_BY_KEY = Object.fromEntries(
  STATUS_OPTIONS.map(status => [status.toLowerCase(), status]),
);
const ACTIONS = {
  Received: [
    ["Accepted", "Accept", "primary"],
    ["Rejected", "Reject", "danger"],
    ["Cancelled", "Cancel", "danger"],
  ],
  Accepted: [
    ["InProgress", "Start Processing", "primary"],
    ["Ready", "Mark Ready", "primary"],
    ["Completed", "Complete Order", "primary"],
    ["Cancelled", "Cancel", "danger"],
  ],
  InProgress: [
    ["Ready", "Mark Ready", "primary"],
    ["Completed", "Complete Order", "primary"],
    ["Cancelled", "Cancel", "danger"],
  ],
  Ready: [
    ["Completed", "Complete Order", "primary"],
    ["Cancelled", "Cancel", "danger"],
  ],
};
const CHECKOUT_NOT_READY_CODE = "OPERATIONAL_STATUS_CHECKOUT_NOT_READY";
const CONCURRENCY_CONFLICT_CODE = "OPERATIONAL_STATUS_CONCURRENCY_CONFLICT";
const ACCEPT_BLOCKED_MESSAGE = "Required fulfilment details are still incomplete.";
const PAYMENT_STATUS_LABEL = {
  created: "Pending",
  paymentRequested: "Payment requested",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  expired: "Expired",
  cancelled: "Cancelled",
  reconciliationRequired: "Reconciliation required",
};
const PAYMENT_STATUS_TONE = {
  created: "border-slate-200 bg-slate-50 text-slate-700",
  paymentRequested: "border-sky-200 bg-sky-50 text-sky-700",
  processing: "border-amber-200 bg-amber-50 text-amber-800",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-rose-200 bg-rose-50 text-rose-700",
  expired: "border-slate-200 bg-slate-100 text-slate-600",
  cancelled: "border-slate-200 bg-slate-100 text-slate-600",
  reconciliationRequired: "border-amber-300 bg-amber-50 text-amber-900",
};
const REQUIREMENT_LABELS = {
  METHOD_REQUIRED: "Fulfilment method",
  RECIPIENT_NAME_REQUIRED: "Recipient name",
  ADDRESS_REQUIRED: "Address line 1",
  CITY_REQUIRED: "City",
  REGION_REQUIRED: "State / Region",
  POSTAL_CODE_REQUIRED: "Postal / PIN code",
};

const pick = (value, camel, pascal = camel[0].toUpperCase() + camel.slice(1)) =>
  value?.[camel] ?? value?.[pascal];

const normalizeStatus = status =>
  status == null ? null : STATUS_BY_KEY[String(status).toLowerCase()] || String(status);

const normalizeContractValue = value => value == null ? null : String(value).trim();

// ASP.NET serializes Commerce enums with camelCase. Accept the earlier PascalCase
// mock/contract shape at the boundary, but keep every UI comparison canonical.
const normalizePaymentEnum = value => {
  const normalized = normalizeContractValue(value);
  return normalized ? normalized[0].toLowerCase() + normalized.slice(1) : null;
};

function formatRequirement(requirement) {
  const value = normalizeContractValue(requirement);
  if (!value) return "Unknown requirement";
  const key = value.toUpperCase();
  if (REQUIREMENT_LABELS[key]) return REQUIREMENT_LABELS[key];
  const readable = value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return readable ? readable[0].toUpperCase() + readable.slice(1).toLowerCase() : value;
}

function formatMoney(value, currency) {
  if (value === null || value === undefined) return "Not priced";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return `${currency || ""} ${Number(value).toLocaleString()}`.trim();
  }
}

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function StatusBadge({ status }) {
  const value = normalizeStatus(status);
  if (!value) {
    return (
      <span className="inline-flex whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">
        Not set
      </span>
    );
  }
  return (
    <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_TONE[value] || STATUS_TONE.Received}`}>
      {STATUS_LABEL[value] || value}
    </span>
  );
}

function SectionHeading({ id, icon: Icon, children, description }) {
  return (
    <div className="flex items-start gap-3">
      {Icon ? (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
          <Icon size={16} aria-hidden="true" />
        </span>
      ) : null}
      <div>
        <h2 id={id} className="text-sm font-semibold text-slate-950">{children}</h2>
        {description ? <p className="mt-0.5 text-xs text-slate-500">{description}</p> : null}
      </div>
    </div>
  );
}

function FulfilmentSection({ detail }) {
  if (!detail) return null;
  const fulfilment = pick(detail, "fulfilment") || null;
  const readiness = pick(detail, "readiness") || {};
  const state = normalizeContractValue(pick(readiness, "readiness"));
  if (state?.toLowerCase() === "notrequired") {
    return (
      <section aria-labelledby="fulfilment-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40">
        <SectionHeading id="fulfilment-title" icon={Package}>Fulfilment</SectionHeading>
        <p className="mt-4 rounded-md bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
          Fulfilment not required
        </p>
      </section>
    );
  }

  const method = normalizeContractValue(
    pick(readiness, "effectiveMethod") ?? pick(fulfilment, "method"),
  );
  const missing = pick(readiness, "missingRequirements") || [];
  const isReady = state?.toLowerCase() === "ready";
  const isDelivery = method?.toLowerCase() === "delivery";
  const statusLabel = state?.toLowerCase() === "awaitingmethod"
    ? "Waiting for fulfilment method"
    : state?.toLowerCase() === "awaitingdeliverydetails"
      ? "Waiting for delivery details"
      : isReady
        ? "Ready"
        : state || "Not available";
  const detailRows = [
    ["Recipient name", pick(fulfilment, "recipientName")],
    ["Recipient phone", pick(fulfilment, "recipientPhone")],
    ["Address line 1", pick(fulfilment, "addressLine1")],
    ["Address line 2", pick(fulfilment, "addressLine2")],
    ["Locality", pick(fulfilment, "locality")],
    ["City", pick(fulfilment, "city")],
    ["State / Region", pick(fulfilment, "region")],
    ["Postal / PIN code", pick(fulfilment, "postalCode")],
    ["Landmark", pick(fulfilment, "landmark")],
  ].filter(([, value]) => typeof value === "string" ? value.trim() : value != null);
  const capturedAt = pick(fulfilment, "detailsCapturedAtUtc") || pick(fulfilment, "updatedAtUtc");

  return (
    <section aria-labelledby="fulfilment-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40">
      <SectionHeading id="fulfilment-title" icon={Package}>Fulfilment</SectionHeading>
      <div className="mt-4 text-sm">
        <dl className="space-y-3">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Method</dt>
            <dd className="text-right font-medium text-slate-900">{method || "Not selected"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Checkout status</dt>
            <dd className={`text-right font-medium ${isReady ? "text-emerald-700" : "text-amber-800"}`}>{statusLabel}</dd>
          </div>
        </dl>
        {missing.length ? (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold uppercase text-slate-500">Still required</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700">
              {missing.map((requirement, index) => (
                <li key={`${requirement}-${index}`}>{formatRequirement(requirement)}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {isReady && isDelivery && detailRows.length ? (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <h3 className="text-xs font-semibold uppercase text-slate-500">Delivery details</h3>
            <dl className="mt-3 space-y-3">
              {detailRows.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-slate-500">{label}</dt>
                  <dd className="mt-0.5 break-words font-medium text-slate-900">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        {isReady && isDelivery && capturedAt ? (
          <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
            Details captured {formatDate(capturedAt)}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function OrderTotalsSection({ totals }) {
  if (!totals) return null;
  const currency = pick(totals, "currency");
  const adjustments = pick(totals, "adjustments") || [];

  return (
    <section aria-labelledby="order-total-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40">
      <SectionHeading id="order-total-title" icon={ShoppingBag}>Order total</SectionHeading>
      <dl className="mt-5 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Items subtotal</dt>
          <dd className="font-medium text-slate-900">{formatMoney(pick(totals, "itemsSubtotal"), currency)}</dd>
        </div>
        {adjustments.map((adjustment) => {
          const kind = normalizeContractValue(pick(adjustment, "kind"));
          const calculationType = normalizeContractValue(pick(adjustment, "calculationType"));
          const isDiscount = kind?.toLowerCase() === "discount";
          const label = calculationType?.toLowerCase() === "percentage"
            ? `${pick(adjustment, "label")} ${Number(pick(adjustment, "configuredValue")).toLocaleString()}%`
            : pick(adjustment, "label");
          return (
            <div key={pick(adjustment, "code")} className="mt-3 flex justify-between gap-4">
              <dt className="text-slate-600">{label}</dt>
              <dd className={`font-medium ${isDiscount ? "text-emerald-700" : "text-slate-900"}`}>
                {isDiscount ? "-" : "+"}{formatMoney(pick(adjustment, "calculatedAmount"), pick(adjustment, "currency") || currency)}
              </dd>
            </div>
          );
        })}
        <div className="mt-5 flex justify-between gap-4 border-t border-slate-200 pt-4 text-base">
          <dt className="font-semibold text-slate-950">Final payable</dt>
          <dd className="text-lg font-bold text-emerald-800">{formatMoney(pick(totals, "finalPayableTotal"), currency)}</dd>
        </div>
      </dl>
    </section>
  );
}

function PaymentSection({ payments, collectionMode, canRequest, allowInitialRequest, onlinePaymentsReady, orderTotals, busy, onRequest }) {
  const attempts = Array.isArray(payments) ? payments : [];
  const latest = attempts.at(-1) || null;
  const status = normalizePaymentEnum(pick(latest, "status"));
  const channel = normalizePaymentEnum(pick(latest, "channel"));
  const isPaid = status === "paid";
  const canRetry = status === "failed" || status === "expired" || status === "cancelled";
  const actionLabel = canRetry ? "Retry payment" : "Send payment";
  const showUnavailable = canRequest && !isPaid && !onlinePaymentsReady &&
    ((allowInitialRequest && !latest) || canRetry);
  const channelLabel = channel === "nativeWhatsApp"
    ? "Native WhatsApp"
    : channel === "paymentLink"
      ? "Payment link"
      : channel === "cashOnDelivery"
        ? "Cash on delivery"
        : channel === "payAtPickup"
          ? "Pay at pickup"
          : null;

  return (
    <section aria-labelledby="payment-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40">
      <div className="flex items-start justify-between gap-3">
        <SectionHeading id="payment-title" icon={CreditCard}>Payment</SectionHeading>
        {latest ? (
          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${PAYMENT_STATUS_TONE[status] || PAYMENT_STATUS_TONE.created}`}>
            {channelLabel && !status ? channelLabel : PAYMENT_STATUS_LABEL[status] || status}
          </span>
        ) : null}
      </div>
      {latest ? (
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex justify-between gap-4"><dt className="text-slate-500">Method</dt><dd className="font-medium text-slate-900">{channelLabel || "Online payment"}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-500">Amount</dt><dd className="font-semibold tabular-nums text-slate-950">{formatMoney(pick(latest, "expectedAmount"), pick(latest, "currency"))}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-500">Attempt</dt><dd className="font-medium text-slate-900">{pick(latest, "attemptNumber")}</dd></div>
          {isPaid && pick(latest, "verifiedAtUtc") ? <div className="border-t border-slate-100 pt-3 text-xs text-slate-500">Verified {formatDate(pick(latest, "verifiedAtUtc"))}</div> : null}
          {status === "reconciliationRequired" ? <div className="border-t border-amber-100 pt-3 text-xs text-amber-800">This attempt needs provider reconciliation before another payment can be requested.</div> : null}
        </dl>
      ) : (
        collectionMode === "manual"
          ? <div className="mt-4"><p className="text-sm font-medium text-slate-800">Payment collection: Manual</p><p className="mt-1 text-sm text-slate-500">This business handles payment outside XploreByte.</p></div>
          : collectionMode === "immediateAfterCheckout"
            ? <p className="mt-4 text-sm text-slate-500">XploreByte requests payment automatically after checkout.</p>
            : <p className="mt-4 text-sm text-slate-500">No payment has been requested.</p>
      )}
      {showUnavailable ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
          {orderTotals ? <p className="text-sm font-semibold text-slate-950">{formatMoney(pick(orderTotals, "finalPayableTotal"), pick(orderTotals, "currency"))}</p> : null}
          <p className="mt-1 text-sm font-semibold text-amber-950">Online payments are not ready.</p>
          <p className="mt-1 text-sm text-amber-900">Connect and configure a payment provider before requesting payment from this customer.</p>
          <a href="/app/settings/order-payments" className="mt-3 inline-flex h-9 items-center justify-center rounded-md border border-amber-300 bg-white px-3 text-sm font-semibold text-amber-950 hover:bg-amber-100">
            Configure payments
          </a>
        </div>
      ) : null}
      {canRequest && onlinePaymentsReady && !isPaid && ((allowInitialRequest && !latest) || canRetry) ? (
        <button type="button" onClick={onRequest} disabled={busy} className="mt-4 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}{actionLabel}
        </button>
      ) : null}
    </section>
  );
}

function EmptyState() {
  return (
    <div className="flex min-h-72 flex-col items-center justify-center px-6 text-center">
      <ShoppingBag className="mb-4 text-slate-400" size={34} />
      <h2 className="text-base font-semibold text-slate-900">No orders yet</h2>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        Orders submitted from your connected WhatsApp catalogue will appear here.
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div role="alert" className="flex min-h-56 flex-col items-center justify-center px-6 text-center">
      <CircleX className="mb-3 text-rose-500" size={30} />
      <p className="text-sm font-medium text-slate-800">{message}</p>
      <button type="button" onClick={onRetry} className="mt-4 inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
        <RefreshCcw size={16} /> Retry
      </button>
    </div>
  );
}

function ReasonDialog({ action, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const required = action?.target === "Rejected" || action?.target === "Cancelled";
  if (!action) return null;
  const valid = !required || reason.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
      <div role="dialog" aria-modal="true" aria-labelledby="order-action-title" className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="order-action-title" className="text-base font-semibold text-slate-900">{action.label}</h2>
            <p className="mt-1 text-sm text-slate-500">Confirm this operational status change.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={busy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {required ? (
          <label htmlFor="order-action-reason" className="mt-5 block text-sm font-medium text-slate-700">
            Reason
            <textarea id="order-action-reason" autoFocus maxLength={200} value={reason} onChange={event => setReason(event.target.value)} rows={4} className="mt-2 w-full resize-none rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100" />
            <span className="mt-1 block text-right text-xs text-slate-400">{reason.length}/200</span>
          </label>
        ) : null}
        {required && action.paymentPaid ? <p role="alert" className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">Payment has already been collected. A manual refund through Razorpay may be required.</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Back</button>
          <button type="button" disabled={busy || !valid} onClick={() => onConfirm(reason.trim() || null)} className={`inline-flex min-w-28 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${action.tone === "danger" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-700 hover:bg-emerald-800"}`}>
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderDetail({ order, payments, paymentConfiguration, loading, error, canEdit, canViewPayments, canRequestPayment, mutationBusy, paymentBusy, notice, onBack, onRetry, onRefresh, onAction, onRequestPayment }) {
  if (loading && !order) return <div role="status" className="flex min-h-96 items-center justify-center"><Loader2 className="animate-spin text-emerald-700" size={28} /><span className="sr-only">Loading order</span></div>;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!order) return null;
  const status = normalizeStatus(pick(order, "operationalStatus"));
  const customer = pick(order, "customer") || {};
  const items = pick(order, "items") || [];
  const totals = pick(order, "totals") || {};
  const orderTotals = pick(order, "orderTotals") || null;
  const history = pick(order, "history") || [];
  const fulfilmentDetail = pick(order, "fulfilment") || null;
  const relatedBooking = pick(order, "relatedBooking") || null;
  const readiness = pick(fulfilmentDetail, "readiness") || {};
  const readinessState = normalizeContractValue(pick(readiness, "readiness"));
  const checkoutGate = Boolean(pick(readiness, "requireCheckoutReadyBeforeAcceptance"));
  const acceptBlocked = status === "Received" && checkoutGate &&
    !["ready", "notrequired"].includes(readinessState?.toLowerCase());
  const actions = canEdit ? ACTIONS[status] || [] : [];
  const collectionMode = normalizePaymentEnum(
    pick(paymentConfiguration, "paymentCollectionMode") || "afterAcceptance",
  );
  const razorpayReadiness = pick(paymentConfiguration, "razorpay") || {};
  const onlinePaymentsReady = Boolean(
    pick(razorpayReadiness, "nativePaymentsReady") ||
    pick(razorpayReadiness, "paymentLinksReady"),
  );
  const latestPaymentStatus = normalizePaymentEnum(pick(payments.at(-1), "status"));
  const paidRefundWarning = latestPaymentStatus === "paid" &&
    (status === "Rejected" || status === "Cancelled");
  return (
    <div className="min-h-full bg-slate-50/70">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <button type="button" onClick={onBack} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"><ArrowLeft size={17} /> Back to orders</button>
      <div className="flex flex-col justify-between gap-5 rounded-lg border border-slate-200 bg-white px-5 py-5 shadow-sm shadow-slate-200/40 sm:px-6 xl:flex-row xl:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-3"><h1 className="text-2xl font-semibold text-slate-950">{pick(order, "reference")}</h1><StatusBadge status={status} /></div>
          <p className="mt-2 text-sm text-slate-500">Received {formatDate(pick(order, "receivedAtUtc"))} · Source: {String(pick(order, "source") || "").toLowerCase() === "api" ? "API" : pick(order, "source")}</p>
          {relatedBooking ? <p className="mt-2 text-sm text-slate-600">Related booking: <span className="font-semibold text-slate-900">{pick(relatedBooking, "reference")}</span> · {pick(relatedBooking, "service")} · {formatDate(pick(relatedBooking, "serviceStartsAtUtc"))}{pick(relatedBooking, "id") ? <a className="mt-2 block w-fit rounded font-semibold text-emerald-700 underline underline-offset-4 focus-visible:outline focus-visible:outline-2" href={bookingWorkspacePath(pick(relatedBooking, "id"))}>Open booking workspace</a> : null}</p> : null}
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onRefresh} disabled={loading} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              <RefreshCcw className={loading ? "animate-spin" : ""} size={16} /> Refresh
            </button>
            {actions.map(([target, label, tone]) => {
              const blocked = target === "Accepted" && acceptBlocked;
              return <button key={target} type="button" disabled={mutationBusy || blocked} aria-describedby={blocked ? "accept-checkout-guidance" : undefined} onClick={() => onAction({ target, label, tone })} className={`h-10 rounded-md px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${tone === "danger" ? "border border-rose-200 bg-white text-rose-700 hover:bg-rose-50" : "bg-emerald-700 text-white hover:bg-emerald-800"}`}>{label}</button>;
            })}
          </div>
          {acceptBlocked ? <p id="accept-checkout-guidance" className="max-w-sm text-sm text-amber-800">{ACCEPT_BLOCKED_MESSAGE}</p> : null}
        </div>
      </div>
      {notice ? <div role="status" className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{notice}</div> : null}
      {paidRefundWarning ? <div role="alert" className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">Payment has already been collected. A manual refund through Razorpay may be required.</div> : null}
      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <section aria-labelledby="items-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40"><SectionHeading id="items-title" icon={ShoppingBag} description={`${items.length} ${items.length === 1 ? "product" : "products"}`}>Order items</SectionHeading><div className="mt-5 divide-y divide-slate-100 border-t border-slate-100">{items.map((item, index) => <div key={`${pick(item, "sku") || pick(item, "itemName")}-${index}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 py-4 last:pb-0"><div><p className="font-semibold text-slate-900">{pick(item, "itemName")}</p><p className="mt-1 text-sm text-slate-500">{pick(item, "variantName") || "Standard"}{pick(item, "sku") ? ` · ${pick(item, "sku")}` : ""}</p><p className="mt-2 text-sm text-slate-600">Qty {Number(pick(item, "quantity")).toLocaleString()} × {formatMoney(pick(item, "unitPrice"), pick(item, "currency"))}</p></div><p className="font-semibold tabular-nums text-slate-950">{formatMoney(pick(item, "lineAmount"), pick(item, "currency"))}</p></div>)}</div></section>
          <section aria-labelledby="history-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40"><SectionHeading id="history-title" icon={CheckCircle2} description="Status changes for this order">Order history</SectionHeading><ol className="mt-5 space-y-0">{history.map((entry, index) => { const historyStatus = normalizeStatus(pick(entry, "newStatus")); return <li key={`${pick(entry, "createdAtUtc")}-${index}`} className="relative flex gap-4 pb-6 last:pb-0"><div className="relative z-10 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 size={15} /></div>{index < history.length - 1 ? <span className="absolute left-[13px] top-8 h-[calc(100%-1.5rem)] w-px bg-slate-200" /> : null}<div><p className="font-medium text-slate-900">{STATUS_LABEL[historyStatus] || historyStatus}</p><p className="mt-0.5 text-sm text-slate-500">{formatDate(pick(entry, "createdAtUtc"))} · {pick(entry, "actor")}</p>{pick(entry, "reason") ? <p className="mt-1 text-sm text-slate-700">{pick(entry, "reason")}</p> : null}</div></li>; })}</ol></section>
        </div>
        <aside className="space-y-5">
          <section aria-labelledby="customer-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40"><SectionHeading id="customer-title" icon={UserRound}>Customer</SectionHeading><div className="mt-4 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600"><UserRound size={18} /></div><div className="min-w-0"><p className="truncate font-semibold text-slate-900">{pick(customer, "displayName")}</p><p className="text-sm text-slate-500">{pick(customer, "phoneNumber")}</p></div></div></section>
          <FulfilmentSection detail={fulfilmentDetail} />
          <section aria-labelledby="summary-title" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40"><SectionHeading id="summary-title" icon={Package}>Summary</SectionHeading><dl className="mt-5 space-y-3 text-sm"><div className="flex justify-between"><dt className="text-slate-500">Products</dt><dd className="font-medium text-slate-900">{pick(totals, "lineCount")}</dd></div><div className="flex justify-between"><dt className="text-slate-500">Total quantity</dt><dd className="font-medium text-slate-900">{Number(pick(totals, "totalQuantity")).toLocaleString()}</dd></div><div className="flex justify-between border-t border-slate-100 pt-3 text-base"><dt className="font-semibold text-slate-900">Items subtotal</dt><dd className="font-semibold tabular-nums text-slate-950">{formatMoney(pick(totals, "total"), pick(totals, "currency"))}</dd></div></dl></section>
          <OrderTotalsSection totals={orderTotals} />
          {canViewPayments ? <PaymentSection payments={payments} collectionMode={collectionMode} canRequest={canRequestPayment && Boolean(orderTotals) && collectionMode !== "manual" && (status === "Accepted" || (collectionMode === "immediateAfterCheckout" && status === "Received"))} allowInitialRequest={collectionMode === "afterAcceptance"} onlinePaymentsReady={onlinePaymentsReady} orderTotals={orderTotals} busy={paymentBusy} onRequest={onRequestPayment} /> : null}
        </aside>
      </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const { can, hasAllAccess, isPlatformSuperAdmin } = useAuth();
  const allowed = useCallback(code => Boolean(hasAllAccess || isPlatformSuperAdmin || can?.(code)), [can, hasAllAccess, isPlatformSuperAdmin]);
  const canEdit = allowed(FK.COMMERCE_TRANSACTION_STATUS_EDIT);
  const canViewPayments = allowed(FK.COMMERCE_PAYMENT_VIEW);
  const canRequestPayment = allowed(FK.COMMERCE_PAYMENT_REQUEST);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [reference, setReference] = useState("");
  const [data, setData] = useState({ items: [], totalCount: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentConfiguration, setPaymentConfiguration] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [action, setAction] = useState(null);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const detailRequestInFlight = useRef(false);
  const paymentOperation = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await ordersApi.list({ page, pageSize: PAGE_SIZE, operationalStatus: status || undefined, reference: reference || undefined })); }
    catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, [page, reference, status]);
  const loadDetail = useCallback(async id => {
    if (detailRequestInFlight.current) return;
    detailRequestInFlight.current = true;
    setDetailLoading(true); setDetailError("");
    try {
      const [nextDetail, nextPayments, nextPaymentConfiguration] = await Promise.all([
        ordersApi.get(id),
        canViewPayments ? commercePaymentsApi.listForOrder(id) : Promise.resolve([]),
        canViewPayments ? commercePaymentsApi.getConfiguration() : Promise.resolve(null),
      ]);
      setDetail(nextDetail);
      setPayments(nextPayments || []);
      setPaymentConfiguration(nextPaymentConfiguration);
    }
    catch (requestError) { setDetailError(requestError.message); }
    finally { detailRequestInFlight.current = false; setDetailLoading(false); }
  }, [canViewPayments]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [loadDetail, selectedId]);
  useEffect(() => {
    paymentOperation.current = null;
  }, [selectedId]);
  useEffect(() => {
    const latestStatus = normalizePaymentEnum(pick(payments.at(-1), "status"));
    if (["failed", "expired", "cancelled"].includes(latestStatus)) {
      paymentOperation.current = null;
    }
  }, [payments]);

  const rows = pick(data, "items") || [];
  const totalCount = Number(pick(data, "totalCount") || 0);
  const totalPages = Number(pick(data, "totalPages") || 0);
  const pageStart = totalCount ? (page - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(totalCount, pageStart + rows.length - 1);
  const submitSearch = event => { event.preventDefault(); setPage(1); setReference(searchDraft.trim()); };
  const openOrder = id => { setNotice(""); setDetail(null); setSelectedId(id); };
  const startAction = next => setAction({ target: next.target, label: next.label, tone: next.tone, paymentPaid: normalizePaymentEnum(pick(payments.at(-1), "status")) === "paid", operationKey: `order-status:${selectedId}:${next.target}:${window.crypto?.randomUUID?.() || Date.now()}` });
  const confirmAction = async reason => {
    if (!detail || !action) return;
    setMutationBusy(true); setNotice("");
    try {
      const transition = await ordersApi.transition(selectedId, { targetStatus: action.target, expectedVersion: pick(detail, "version"), operationKey: action.operationKey, reason });
      setAction(null);
      if (pick(transition, "manualRefundMayBeRequired")) {
        setNotice("Payment has already been collected. A manual refund through Razorpay may be required.");
      }
      await Promise.all([loadDetail(selectedId), loadList()]);
    } catch (requestError) {
      setAction(null);
      if (requestError.status === 409) {
        if (requestError.code === CHECKOUT_NOT_READY_CODE) {
          setNotice("This order can't be accepted yet. Required fulfilment details are still incomplete.");
        } else if (requestError.code === CONCURRENCY_CONFLICT_CODE) {
          setNotice("This order was updated by someone else. We've refreshed the latest status.");
        } else {
          setNotice(requestError.message);
        }
        await Promise.all([loadDetail(selectedId), loadList()]);
      } else setNotice(requestError.message);
    } finally { setMutationBusy(false); }
  };

  const requestPayment = async () => {
    if (!selectedId || paymentBusy) return;
    setPaymentBusy(true); setNotice("");
    try {
      if (!paymentOperation.current) {
        paymentOperation.current =
          `payment-request:${selectedId}:${window.crypto?.randomUUID?.() || Date.now()}`;
      }
      await commercePaymentsApi.requestPayment(selectedId, paymentOperation.current);
      await loadDetail(selectedId);
    } catch (requestError) {
      setNotice(requestError?.response?.data?.message || requestError?.message || "Payment could not be requested.");
      if (requestError?.status === 409 || requestError?.response?.status === 409) {
        await loadDetail(selectedId);
      }
    } finally { setPaymentBusy(false); }
  };

  if (selectedId) return <><OrderDetail order={detail} payments={payments} paymentConfiguration={paymentConfiguration} loading={detailLoading} error={detailError} canEdit={canEdit} canViewPayments={canViewPayments} canRequestPayment={canRequestPayment} mutationBusy={mutationBusy} paymentBusy={paymentBusy} notice={notice} onBack={() => { setSelectedId(null); setDetail(null); setPayments([]); setPaymentConfiguration(null); setNotice(""); }} onRetry={() => loadDetail(selectedId)} onRefresh={() => loadDetail(selectedId)} onAction={startAction} onRequestPayment={requestPayment} /><ReasonDialog action={action} busy={mutationBusy} onClose={() => setAction(null)} onConfirm={confirmAction} /></>;

  const handleRowKeyDown = (event, id) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openOrder(id);
    }
  };

  return (
    <div className="min-h-full bg-slate-50/70">
      <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
      <header>
        <h1 className="text-2xl font-semibold text-slate-950">Orders</h1>
        <p className="mt-1.5 text-sm text-slate-500">Review incoming orders and manage customer orders.</p>
      </header>
      <div className="mt-6 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm shadow-slate-200/40 sm:flex-row sm:items-center">
        <form onSubmit={submitSearch} className="relative flex-1"><Search className="pointer-events-none absolute left-3 top-2.5 text-slate-400" size={18} /><input aria-label="Search order reference" value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder="Search order reference" className="h-10 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none transition-shadow focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100" /></form>
        <select aria-label="Filter by status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }} className="h-10 min-w-44 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"><option value="">All statuses</option>{STATUS_OPTIONS.map(option => <option key={option} value={option}>{STATUS_LABEL[option]}</option>)}</select>
        <button type="button" onClick={loadList} disabled={loading} className="inline-flex h-10 w-fit items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"><RefreshCcw className={loading ? "animate-spin" : ""} size={16} /> Refresh</button>
      </div>
      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm shadow-slate-200/40">
        {loading ? <div role="status" aria-label="Loading orders" className="flex min-h-72 items-center justify-center"><Loader2 className="animate-spin text-emerald-700" size={28} /></div> : error ? <ErrorState message={error} onRetry={loadList} /> : !rows.length ? <EmptyState /> : <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50/80 text-xs font-semibold uppercase text-slate-500"><tr><th className="px-5 py-3.5">Order</th><th className="px-5 py-3.5">Customer</th><th className="px-5 py-3.5">Items</th><th className="px-5 py-3.5">Total</th><th className="px-5 py-3.5">Received</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Action</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(order => {
                  const id = pick(order, "id");
                  const referenceValue = pick(order, "reference");
                  const lineCount = Number(pick(order, "lineCount") || 0);
                  return (
                    <tr key={id} tabIndex={0} aria-label={`Open order ${referenceValue}`} onClick={() => openOrder(id)} onKeyDown={event => handleRowKeyDown(event, id)} className="group cursor-pointer transition-colors hover:bg-emerald-50/40 focus-visible:bg-emerald-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500">
                      <td className="px-5 py-4 font-semibold text-slate-950">{referenceValue}</td>
                      <td className="px-5 py-4"><p className="font-medium text-slate-800">{pick(order, "customerName")}</p><p className="mt-0.5 text-xs text-slate-500">{pick(order, "customerPhone")}</p></td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-600">{lineCount} {lineCount === 1 ? "product" : "products"} · {Number(pick(order, "totalQuantity")).toLocaleString()} qty</td>
                      <td className="whitespace-nowrap px-5 py-4 font-semibold tabular-nums text-slate-900">{formatMoney(pick(order, "total"), pick(order, "currency"))}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-600">{formatDate(pick(order, "receivedAtUtc"))}</td>
                      <td className="px-5 py-4"><StatusBadge status={pick(order, "operationalStatus")} /></td>
                      <td className="px-5 py-4 text-right"><button type="button" aria-label="View" title={`View details for ${referenceValue}`} onClick={event => { event.stopPropagation(); openOrder(id); }} onKeyDown={event => event.stopPropagation()} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 font-medium text-emerald-700 transition-colors hover:bg-emerald-100/70 group-hover:text-emerald-800"><Eye size={16} /> View details</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-slate-100 md:hidden">{rows.map(order => { const lineCount = Number(pick(order, "lineCount") || 0); return <button type="button" key={pick(order, "id")} onClick={() => openOrder(pick(order, "id"))} className="block w-full p-4 text-left transition-colors hover:bg-emerald-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"><div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-slate-950">{pick(order, "reference")}</p><p className="mt-1 text-sm text-slate-600">{pick(order, "customerName")}</p><p className="mt-0.5 text-xs text-slate-500">{pick(order, "customerPhone")}</p></div><StatusBadge status={pick(order, "operationalStatus")} /></div><div className="mt-4 flex items-end justify-between gap-4"><p className="text-sm text-slate-500">{lineCount} {lineCount === 1 ? "product" : "products"} · {Number(pick(order, "totalQuantity")).toLocaleString()} qty</p><p className="font-semibold tabular-nums text-slate-950">{formatMoney(pick(order, "total"), pick(order, "currency"))}</p></div></button>; })}</div>
        </>}
      </div>
      {!loading && !error && totalCount ? <div className="mt-4 flex flex-col items-center justify-between gap-3 text-sm text-slate-500 sm:flex-row"><span>Showing {pageStart}-{pageEnd} of {totalCount} orders</span><div className="flex items-center gap-2"><button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(current => current - 1)} className="rounded-md border border-slate-300 p-2 text-slate-700 hover:bg-white disabled:opacity-40"><ChevronLeft size={17} /></button><span>Page {page} of {Math.max(totalPages, 1)}</span><button type="button" aria-label="Next page" disabled={!totalPages || page >= totalPages} onClick={() => setPage(current => current + 1)} className="rounded-md border border-slate-300 p-2 text-slate-700 hover:bg-white disabled:opacity-40"><ChevronRight size={17} /></button></div></div> : null}
      </div>
    </div>
  );
}
